<template>
  <div :class="$style.scroll">
    <ul :class="$style.tabs" role="tablist">
      <li
        v-for="stage in stages"
        :key="stage.id"
        :class="$style.contents"
        role="presentation"
      >
        <button
          type="button"
          :class="[
            $style.card,
            $style[`state-${stage.ui_state}`],
            { [$style.active]: stage.id === modelValue }
          ]"
          role="tab"
          :aria-selected="stage.id === modelValue"
          @click="emit('update:modelValue', stage.id)"
        >
          <span :class="$style.head">
            <span
              :class="[
                $style.title,
                { [$style.titleFinished]: isTournamentFinished }
              ]"
            >
              {{ stage.name }}
            </span>
            <span :class="$style.headRight">
              <ui-icon
                v-if="stage.ui_state === 'finished'"
                name="checks"
                :class="$style.iconCheck"
              />
              <ui-icon
                v-else-if="stage.ui_state === 'awaiting_results'"
                name="lock-circled"
                :class="$style.iconLock"
              />
              <pickem-countdown
                v-else-if="timerTo(stage)"
                :to="timerTo(stage)"
                :class="$style.timer"
              >
                <ui-icon name="clock-filled" :class="$style.timerIcon" />
              </pickem-countdown>
            </span>
          </span>

          <span :class="$style.foot">
            <ui-icon
              v-if="stage.ui_state === 'awaiting_results'"
              name="play-filled"
              :class="$style.iconPlay"
            />
            <ui-icon
              v-else-if="stage.ui_state === 'locked'"
              name="lock-circled"
              :class="$style.iconLockSm"
            />
            <span :class="$style.subtitle">{{ subtitleOf(stage) }}</span>
            <span
              v-if="stage.ui_state === 'finished' && stage.user_xp"
              :class="$style.xpEarned"
            >
              <ui-icon name="xp" :class="$style.iconXp" />
              {{ formatNumber(stage.user_xp) }}
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import UiIcon from '@/ui-core/components/icon/index.vue'
import PickemCountdown from '../common/Countdown.vue'

import type { StageSummary } from '../../types'

defineOptions({ name: 'PickemStagesTabs' })

defineProps<{
  stages: StageSummary[]
  isTournamentFinished: boolean
  modelValue: number | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const subtitleOf = (stage: StageSummary): string => {
  switch (stage.ui_state) {
    case 'finished':
      return 'Завершён'
    case 'awaiting_open':
      return 'Скоро откроется'
    case 'awaiting_results':
      return 'Уже идёт'
    case 'locked':
      return 'Разблокировать'
    case 'pickable':
      if (stage.prediction_status === 'full') return 'Все команды выбраны'
      if (stage.prediction_status === 'partial') {
        return `${stage.slots_filled} / ${stage.slots_total}`
      }
      return 'Выбери команды'
  }
}

// Таймер показываем только когда этап «активный» и lock_at в будущем —
// это даёт «5ч 13м» из макета над зелёным акцентом. Для locked-этапа
// время разблокировки в `StageSummary` не приходит (только условия),
// поэтому таймер для него не рисуем.
const timerTo = (stage: StageSummary): number | null => {
  if (
    (stage.ui_state === 'pickable' || stage.ui_state === 'locked') &&
    stage.lock_at &&
    stage.lock_at * 1000 > Date.now()
  ) {
    return stage.lock_at
  }

  return null
}

const formatNumber = (n: number) => new Intl.NumberFormat('ru-RU').format(n)
</script>

<style lang="scss" module>
@use './variables' as vars;

.scroll {
  margin: 0 calc(var(--pickem-details-padding) * -1);
  padding: 0 var(--pickem-details-padding);
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tabs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(16em, 1fr);
  gap: 0.8em;
  min-width: 100%;

  @include down(sm) {
    display: flex;
    width: fit-content;
  }
}

.contents {
  display: contents;
}

.card {
  appearance: none;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.6em;
  padding: 1.2em 1.4em;
  border-radius: 1em;
  background-color: color-get(--root-rgb-bg-card);
  color: color-get(--root-rgb-text-1);
  border: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  min-height: 6.4em;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  @include hover-focus {
    border-color: color-get(--root-rgb-bg-border);
  }

  @include down(sm) {
    min-width: 16em;
  }
}

.active {
  border-color: color-get(--root-rgb-accent-2);
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4em;

  @include down(sm) {
    width: max-content;
    gap: 7em;
  }
}

.title {
  @include typo-get(subhead-s);

  @include down(sm) {
    @include typo-get(subhead-m);
  }
}

.titleFinished {
  opacity: 0.3;
}

.headRight {
  display: inline-flex;
  align-items: center;
}

.iconCheck {
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-accent-2);
}

.iconLock {
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-text-3);
}

.iconLockSm {
  width: 1.2em;
  height: 1.2em;
  color: color-get(--root-rgb-accent-2);
}

.iconPlay {
  width: 1.2em;
  height: 1.2em;
  color: color-get(--root-rgb-system-warning);
}

.iconXp {
  width: 1em;
  color: color-get(--root-rgb-accent-2);
}

.timer {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  border-radius: 0.6em;
  color: color-get(--root-rgb-text-1);
  @include typo-get(body-xs);
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
}

.timerIcon {
  width: 0.9em;
  color: color-get(--root-rgb-system-success);
}

.foot {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);
}

.xpEarned {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  color: color-get(--root-rgb-text-1);
  font-variant-numeric: tabular-nums;
}

// Цветные акценты на subtitle по состоянию
.state-finished .subtitle {
  color: color-get(--root-rgb-text-3);
}

.state-pickable .subtitle {
  color: color-get(--root-rgb-text-1);
}

.state-locked {
  .subtitle {
    color: color-get(--root-rgb-accent-4);
  }

  .iconLockSm {
    color: color-get(--root-rgb-accent-4);
  }
}

.state-awaiting_results .subtitle {
  color: color-get(--root-rgb-system-warning);
  font-weight: 400;
}
</style>
